import type { AxiosInstance } from "axios";
import axios from "axios";
import { errorResponse } from "./api-interface";
import { getRegion } from "./region-config";

type BaseUrlKey =
  | "apiUrl"
  | "aiUrl"
  | "fileuploadUrl"
  | "platformUrl"
  | "platformServiceUrl"
  | "signalRUrl"
  | "dfdApiUrl";

const REGION_BASE_URLS: Record<string, Record<BaseUrlKey, string>> = {
  us: {
    apiUrl: import.meta.env.VITE_API_URL_US,
    aiUrl: import.meta.env.VITE_AI_URL,
    fileuploadUrl: import.meta.env.VITE_UPLOAD_API_URL,
    platformUrl: import.meta.env.VITE_PLATFORM_URL_US,
    platformServiceUrl: import.meta.env.VITE_PLATFORM_SERVICE_URL_US,
    signalRUrl: import.meta.env.VITE_SIGNALR_URL,
    dfdApiUrl: import.meta.env.VITE_DFD_API_US,
  },
  de: {
    apiUrl: import.meta.env.VITE_API_URL_DE,
    aiUrl: import.meta.env.VITE_AI_URL,
    fileuploadUrl: import.meta.env.VITE_UPLOAD_API_URL,
    platformUrl: import.meta.env.VITE_PLATFORM_URL_DE,
    platformServiceUrl: import.meta.env.VITE_PLATFORM_SERVICE_URL_DE,
    signalRUrl: import.meta.env.VITE_SIGNALR_URL,
    dfdApiUrl: import.meta.env.VITE_DFD_API_DE,
  },
  jp: {
    apiUrl: import.meta.env.VITE_API_URL_JP,
    aiUrl: import.meta.env.VITE_AI_URL,
    fileuploadUrl: import.meta.env.VITE_UPLOAD_API_URL,
    platformUrl: import.meta.env.VITE_PLATFORM_URL_JP,
    platformServiceUrl: import.meta.env.VITE_PLATFORM_SERVICE_URL_JP,
    signalRUrl: import.meta.env.VITE_SIGNALR_URL,
    dfdApiUrl: import.meta.env.VITE_DFD_API_JP,
  }
};

const getBaseUrl = (baseUrlKey: BaseUrlKey): string => {
  const region = getRegion() ?? import.meta.env.VITE_DEFAULT_REGION ?? "us";
  const regionUrls =
    REGION_BASE_URLS[region] ||
    REGION_BASE_URLS[import.meta.env.VITE_DEFAULT_REGION || "us"] ||
    REGION_BASE_URLS.us;

  return regionUrls[baseUrlKey];
};

export const getAxiosInstance = (
  baseUrlKey: BaseUrlKey = "apiUrl",
  accessToken: string = "",
  totalParts?: string,
): AxiosInstance => {
  const baseURL = getBaseUrl(baseUrlKey);

  const token = accessToken || import.meta.env.VITE_SESSION_TOKEN;

  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  const isPlatformRequest =
    baseUrlKey === "fileuploadUrl" ||
    baseUrlKey === "platformUrl" ||
    baseUrlKey === "platformServiceUrl";

  const isDfdRequest = baseUrlKey === "dfdApiUrl";

  if (isPlatformRequest) {
    if (totalParts) headers["x-total-parts"] = totalParts;
  } else if (isDfdRequest) {
    headers["X-API-KEY"] = import.meta.env.VITE_DFD_API_KEY;
    headers["content-type"] = "application/json";
  } else {
    headers["X-API-KEY"] = import.meta.env.VITE_API_KEY;
    headers["content-type"] = "application/json";
  }

  const instance = axios.create({
    baseURL,
    headers,
  });

  instance.interceptors.request.use(async (config) => {
    const url = config.url || "";

    if (url.startsWith("data:")) {
      throw new Error("Blocked unsafe data URL to prevent DoS vulnerability");
    }

    try {
      const { msalInstance } = await import("../helpers/msalConfig");
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        const response = await msalInstance.acquireTokenSilent({
          scopes: [import.meta.env.VITE_AUTH_SCOPE],
          account: accounts[0],
          forceRefresh: false,
        });

        if (response?.accessToken) {
          config.headers.Authorization = `Bearer ${response.accessToken}`;
        }
      }
    } catch {
      // fallback to existing token
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error)) {
        const response = errorResponse(
          error.response?.data?.message ?? error.message,
          {
            status: error.response?.status,
            code: error.code,
            data: error.response?.data,
          },
        );

        return Promise.reject(new Error(JSON.stringify(response)));
      }

      const response = errorResponse("Unexpected error occurred");
      return Promise.reject(new Error(JSON.stringify(response)));
    },
  );

  return instance;
};
``